import { useState, useRef, useEffect, FormEvent } from 'react';
import { services } from '@/mocks/services';

const ContactSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((v, k) => body.append(k, v.toString()));
    setLoading(true);
    try {
      await fetch('https://readdy.ai/api/form/d76oe5l4hjod66j2eung', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 md:py-28 bg-brand-dark">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-amber/30 rounded-full mb-4">
            <span className="text-xs font-semibold text-brand-amber uppercase tracking-widest">Free Consultation</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Let's Build Something <span className="text-brand-amber italic">Extraordinary</span>
          </h2>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Ready to transform your property? Contact Valley View Construction for your free, no-obligation consultation and quote.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-5 gap-10 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {[
              { icon: 'ri-phone-fill', label: 'Call Us', value: '574-409-6118', href: 'tel:+15744096118', sub: 'Mon–Fri 7am–6pm, Sat 8am–4pm' },
              { icon: 'ri-mail-fill', label: 'Email Us', value: 'valleyviewconstruction98@gmail.com', href: 'mailto:valleyviewconstruction98@gmail.com', sub: 'We respond within 24 hours' },
              { icon: 'ri-map-pin-2-fill', label: 'Location', value: 'Elkhart, Indiana', href: '', sub: 'Serving 30-mile radius throughout Michiana' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4 p-5 bg-brand-charcoal rounded-xl border border-white/8">
                <div className="w-11 h-11 flex items-center justify-center bg-brand-amber/15 rounded-xl flex-shrink-0">
                  <i className={`${item.icon} text-brand-amber text-lg`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-brand-amber uppercase tracking-wider mb-1">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-white font-semibold hover:text-brand-amber transition-colors block text-sm break-all">{item.value}</a>
                  ) : (
                    <p className="text-white font-semibold text-sm">{item.value}</p>
                  )}
                  <p className="text-white/50 text-xs mt-0.5">{item.sub}</p>
                </div>
              </div>
            ))}

            <div className="p-5 bg-brand-charcoal rounded-xl border border-white/8">
              <p className="text-xs font-semibold text-brand-amber uppercase tracking-wider mb-3">Follow Us</p>
              <a href="https://www.facebook.com" target="_blank" rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/8 hover:bg-brand-amber rounded-lg text-white font-medium text-sm transition-all">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-facebook-fill" />
                </div>
                Follow Valley View on Facebook
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3 bg-brand-charcoal rounded-2xl p-8 border border-white/8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-amber/20 rounded-full mb-4">
                  <i className="ri-check-line text-brand-amber text-3xl" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-white mb-2">Message Sent!</h3>
                <p className="text-white/60 text-sm max-w-xs">Thank you! We'll get back to you within 24 hours to discuss your project.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 px-6 py-2.5 border border-white/20 text-white/70 hover:text-white rounded-lg text-sm transition-colors cursor-pointer">Send Another Message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form action="https://readdy.ai/api/form/d76oe5l4hjod66j2eung" method="POST" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">Full Name *</label>
                    <input required name="name" type="text" placeholder="John Smith" className="w-full px-4 py-3 bg-brand-dark border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-amber transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">Phone</label>
                    <input name="phone" type="tel" placeholder="574-000-0000" className="w-full px-4 py-3 bg-brand-dark border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-amber transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">Email Address *</label>
                  <input required name="email" type="email" placeholder="you@email.com" className="w-full px-4 py-3 bg-brand-dark border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-amber transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">Service Interested In</label>
                  <select name="service" className="w-full px-4 py-3 bg-brand-dark border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-brand-amber transition-colors cursor-pointer">
                    <option value="">Select a service...</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                    <option value="Not Sure">Not Sure Yet — Need Consultation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">Project Details</label>
                  <textarea
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your project, location, timeline, and any other details..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 bg-brand-dark border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-amber transition-colors resize-none"
                  />
                  <p className="text-xs text-white/30 text-right mt-1">{charCount}/500</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-brand-amber hover:bg-brand-amber-light disabled:opacity-60 text-white font-semibold rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2 text-base whitespace-nowrap"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-fill" /> Send My Free Quote Request</>
                  )}
                </button>
                <p className="text-center text-white/40 text-xs">No spam, ever. We'll respond within 24 hours.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
